import { get, patch, post } from '~/shared/http-client/api'
import {
  type ApiResponse,
  type AuthApiResponse,
} from '~/shared/http-client/exceptions/type'

// Request Type

export interface SignUpRequest {
  userInfo: {
    name: string
    nickname: string
    email: string
    password: string
    gender: string
    year_of_birth: string
  }
  key: string
  certCode: string
}
export interface SignInRequest {
  userInfo: {
    email: string
    password: string
  }
}

export interface SignOutRequest {
  refreshToken: string
}

export interface SocialLoginRequest {
  code: string
  type: string
}

export interface RefreshTokenRequest {
  refreshToken: string
}

export interface PasswordResetRequest {
  key: string
  certCode: string
  new_password: string
}

export type Session = {
  accessToken: string
  refreshToken: string
}

export type UserInfo = {
  id: number
  name: string
  nickname: string
  email: string
  year_of_birth: number | null
  gender: string
  profile_image: string | null
  status: string | null
  type: string
  social_login_id: string | null
  created_at: string
  updated_at: string | null
}

// Response Type

export interface CheckCertResponse extends ApiResponse {
  data: {
    key: string
  }
}

export interface AuthResponse extends ApiResponse {
  data: {
    userInfo: UserInfo
    session?: Session
  }
}

export interface SignOutResponse extends ApiResponse {
  data: {}
}

export interface PasswordChangeResponse extends ApiResponse {
  data: {
    key: string
  }
}

export const signUp = async (signUpInfo: SignUpRequest) => {
  const response: AuthResponse = await post('/auth/join', signUpInfo)

  return response
}

export const signIn = async (signInInfo: SignInRequest) => {
  const response: AuthResponse = await post('/auth/login', signInInfo)

  return response
}

export const signOut = async (signOutInfo: SignOutRequest) => {
  const response: SignOutResponse = await post('/auth/logout', signOutInfo)

  return response
}

export const socialLogin = async (socialLoginInfo: SocialLoginRequest) => {
  const response: AuthResponse = await post(
    '/auth/login/social',
    socialLoginInfo,
  )

  return response
}

export const checkEmailCert = async (email: string) => {
  const response: CheckCertResponse = await get(`/auth/join/sendCert/${email}`)

  return response
}

export const checkCertCode = async ({
  key,
  code,
}: {
  key: string
  code: string
}) => {
  const response: ApiResponse = await get(`/auth/join/checkCert/${key}/${code}`)

  return response
}

export const nicknameDuplicateCheck = async (nickname: string) => {
  const response: ApiResponse = await get(`/auth/check/nickname/${nickname}`)

  return response
}

export const renewalRefreshToken = async (refreshToken: string) => {
  const response: AuthApiResponse = await post('/auth/refreshToken', {
    refreshToken,
  })

  if (response.errorCode === 0) {
    localStorage.setItem(
      'starlike-access-token',
      response.data.session.accessToken,
    )
    localStorage.setItem(
      'starlike-refresh-token',
      response.data.session.refreshToken,
    )
  }

  return response
}

export const changePassword = async (email: string) => {
  const response: PasswordChangeResponse = await get(
    `/auth/password/sendCert/${email}`,
  )

  return response
}

export const sendkeyCodePasswordChange = async ({
  key,
  code,
}: {
  key: string
  code: string
}) => {
  const response: ApiResponse = await get(
    `/auth/password/checkCert/${key}/${code}`,
  )

  return response
}

export const resetPassword = async ({
  key,
  certCode,
  new_password,
}: PasswordResetRequest) => {
  const response: ApiResponse = await patch('/auth/password', {
    userInfo: {
      key,
      certCode,
      new_password,
    },
  })

  return response
}
